import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  type IconName,
} from "@nexus/ui";
import type { BigQueryUsageMonitoring } from "@/features/bigquery-usage/api/bigquery-usage.api";
import {
  summarizeBigQueryReconciliation,
  type BigQueryReconciliationStatus,
} from "../lib/reconciliation";
import { BigQueryReconciliationPanel } from "./BigQueryReconciliationPanel";

export interface BigQueryReconciliationDialogProps {
  /** Undefined until the Cloud Monitoring read resolves. */
  monitoring: BigQueryUsageMonitoring | undefined;
  isLoading: boolean;
  /** What Nexus recorded for the same window. */
  recordedQueries: number;
  recordedBytesBilled: number;
  /**
   * Whether a narrowing is active. Cloud Monitoring accounts for the whole GCP
   * project and takes no filter, so a narrowed recorded total has nothing
   * comparable on the other side.
   */
  isNarrowed: boolean;
}

/**
 * Two states of the tab's own, on top of the summary's three.
 *
 * `narrowed` is not Monitoring's business: it decides whether a verdict may be
 * reported at all. `pending` exists because an unresolved read summarises as
 * `unavailable`, and a button that says the window could not be reconciled while
 * the read is still in flight states a failure that has not happened.
 */
type TriggerState = "pending" | "narrowed" | BigQueryReconciliationStatus;

const STATE_ICON: Record<TriggerState, IconName> = {
  pending: "loader",
  // Points at the controls the admin can reach, like the zero-state notice's.
  narrowed: "filter",
  unavailable: "info",
  agreement: "check-circle",
  discrepancy: "alert-circle",
};

/**
 * The reconciliation, folded into the filter row.
 *
 * It answers one question — do Nexus's totals account for what Google billed
 * this project for — and the answer is a single sentence, so the sentence goes
 * on the button and the two comparisons behind it stay out of the page's height.
 * Nothing an operator has to act on is hidden: a disagreement says so before it
 * is opened.
 *
 * Reading it before the figures rather than after them is also why it sits here
 * rather than at the foot of the tab, which is where it used to be.
 */
export function BigQueryReconciliationDialog({
  monitoring,
  isLoading,
  recordedQueries,
  recordedBytesBilled,
  isNarrowed,
}: BigQueryReconciliationDialogProps) {
  const { t } = useTranslation();

  const summary = useMemo(
    () =>
      summarizeBigQueryReconciliation(monitoring, {
        queries: recordedQueries,
        bytesBilled: recordedBytesBilled,
      }),
    [monitoring, recordedQueries, recordedBytesBilled],
  );

  const isPending = isLoading && !monitoring;
  const state: TriggerState = isPending ? "pending" : isNarrowed ? "narrowed" : summary.status;

  const label =
    state === "pending"
      ? t("admin.bigQueryUsage.reconciliation.pendingLabel", "Reconciling with Google")
      : state === "narrowed"
        ? t(
            "admin.bigQueryUsage.reconciliation.narrowedLabel",
            "Nothing to reconcile while filtered",
          )
        : state === "unavailable"
          ? t("admin.bigQueryUsage.reconciliation.unavailableLabel", "Not reconciled")
          : state === "agreement"
            ? t("admin.bigQueryUsage.reconciliation.agreementLabel", "Agrees with Google")
            : t(
                "admin.bigQueryUsage.reconciliation.discrepancyLabel",
                "Does not agree with Google",
              );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          // Colour is the secondary signal: the sentence itself already says
          // whether the two sides agree.
          className={cn("text-xs", state === "discrepancy" && "text-destructive")}
          disabled={isPending}
        >
          <Icon
            name={STATE_ICON[state]}
            size="xs"
            color={state === "discrepancy" ? "destructive" : "muted"}
            className={cn(state === "pending" && "animate-spin motion-reduce:animate-none")}
            aria-hidden
          />
          {label}
        </Button>
      </DialogTrigger>

      <DialogContent variant="form">
        <DialogHeader>
          <DialogTitle>
            {t("admin.bigQueryUsage.reconciliation.title", "Nexus totals against Google's")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.bigQueryUsage.reconciliation.dialogDescription",
              "Cloud Monitoring accounts for every query this Google Cloud project ran. Nexus records only the ones it ran itself. This compares the two.",
            )}
          </DialogDescription>
        </DialogHeader>

        <BigQueryReconciliationPanel
          monitoring={monitoring}
          isLoading={isLoading}
          recordedQueries={recordedQueries}
          recordedBytesBilled={recordedBytesBilled}
          isNarrowed={isNarrowed}
        />
      </DialogContent>
    </Dialog>
  );
}
